import styled from 'foundations-theming/utils';
import { getReferenceBasePadding, getReferenceVariantStyles } from './Select.helpers';
import { deepMerge } from '../../transitional/utils/deepMerge';
import { JUPITER_LAYER, VENUS_LAYER } from '../../transitional/constants';
import { HEIGHT } from '../../constants/nonTokenisedValues';
import { getFocusStyleObject } from '../../transitional/utils/focus/focus.style';
export const StyledContainer = styled.div.withConfig({
  displayName: "Selectstyles__StyledContainer",
  componentId: "cam4x1-0"
})(() => {
  return {
    inlineSize: '100%'
  };
});
export const StyledReferenceContainer = styled.div.withConfig({
  displayName: "Selectstyles__StyledReferenceContainer",
  componentId: "cam4x1-1"
})(({
  $marginStyles
}) => {
  return Object.assign({}, $marginStyles);
});
export const StyledReferenceLabelWrapper = styled.label.withConfig({
  displayName: "Selectstyles__StyledReferenceLabelWrapper",
  componentId: "cam4x1-2"
})(({
  theme
}) => {
  return {
    lineHeight: theme.typography['heading-50'].lineHeight,
    display: 'flex',
    justifyContent: 'space-between',
    marginBlockEnd: theme.space[100]
  };
});
export const StyledReferenceWrapper = styled.div.withConfig({
  displayName: "Selectstyles__StyledReferenceWrapper",
  componentId: "cam4x1-3"
})(({
  theme,
  $referenceSize
}) => {
  const blockSize = $referenceSize === 'compact' ? theme.space[800] : theme.space[1000];
  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    inlineSize: '100%',
    blockSize
  };
});
export const StyledReference = styled.button.withConfig({
  displayName: "Selectstyles__StyledReference",
  componentId: "cam4x1-4"
})(({
  theme,
  $referenceVariant,
  $error
}) => {
  const {
    paddingBlock,
    paddingInline
  } = getReferenceBasePadding(theme);
  const referenceVariantStyles = getReferenceVariantStyles(theme, $referenceVariant);
  return Object.assign({
    background: 'none',
    font: 'inherit',
    cursor: 'pointer',
    outline: 'inherit',
    position: 'relative',
    overflow: 'hidden',
    inlineSize: '100%',
    blockSize: 'inherit',
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    color: theme.color['text-core-default'],
    paddingBlock,
    paddingInline
  }, referenceVariantStyles, {
    '&:active:not(:disabled), &:focus:not(:disabled)': {
      borderColor: theme.color['border-interactive-default']
    },
    '&:disabled': {
      cursor: 'not-allowed',
      color: theme.color['text-primary-disabled']
    }
  }, $error && {
    borderColor: theme.color['border-alert-default']
  });
});
export const StyledReferenceSupportText = styled.span.withConfig({
  displayName: "Selectstyles__StyledReferenceSupportText",
  componentId: "cam4x1-5"
})(({
  theme,
  $error
}) => {
  return Object.assign({}, theme.typography['heading-25'], {
    display: 'block',
    color: theme.color['text-core-subtle'],
    marginBlockStart: theme.space[100],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }, $error && {
    color: theme.color['text-alert-default']
  });
});
export const StyledReferenceIndicator = styled.div.withConfig({
  displayName: "Selectstyles__StyledReferenceIndicator",
  componentId: "cam4x1-6"
})(({
  theme,
  $referenceSize
}) => {
  const {
    paddingBlock
  } = getReferenceBasePadding(theme);
  const referenceHeight = $referenceSize === 'default' ? theme.space[1000] : theme.space[800];
  return {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    blockSize: `calc(${referenceHeight} - ${paddingBlock} * 2)`,
    borderRadius: theme.borderRadius['full'],
    flexShrink: 0
  };
});
export const StyledPlaceholder = styled.span.withConfig({
  displayName: "Selectstyles__StyledPlaceholder",
  componentId: "cam4x1-7"
})(({
  theme,
  $hasSelectedValues
}) => {
  return {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginInlineEnd: $hasSelectedValues ? theme.space[600] : theme.space[200]
  };
});
export const StyledDownCaretIconWrapper = styled.span.withConfig({
  displayName: "Selectstyles__StyledDownCaretIconWrapper",
  componentId: "cam4x1-8"
})(({
  $size,
  $selectStylesOverrides = {}
}) => {
  return deepMerge({
    display: 'flex',
    alignItems: 'center',
    opacity: 0.8,
    inlineSize: $size,
    blockSize: $size
  }, $selectStylesOverrides);
});
export const StyledRemoveIconWrapper = styled.span.withConfig({
  displayName: "Selectstyles__StyledRemoveIconWrapper",
  componentId: "cam4x1-9"
})(({
  theme,
  $size
}) => {
  return {
    all: 'unset',
    position: 'absolute',
    insetInlineEnd: '8px',
    insetBlockStart: '50%',
    transform: 'translateY(-50%)',
    inlineSize: $size,
    blockSize: $size,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    padding: theme.space[100],
    border: `${theme.borderWidth['100']} solid ${theme.color['fill-transparent-default']}`,
    '&:focus-visible': {
      borderColor: theme.color['border-interactive-default'],
      borderRadius: theme.borderRadius['full']
    }
  };
});
export const StyledTextInputWrapper = styled.div.withConfig({
  displayName: "Selectstyles__StyledTextInputWrapper",
  componentId: "cam4x1-10"
})(({
  theme,
  $iconSize
}) => {
  return {
    position: 'absolute',
    insetBlockStart: 0,
    insetInlineStart: 0,
    insetInlineEnd: 0,
    zIndex: VENUS_LAYER,
    inlineSize: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: theme.space[200],
    backgroundColor: theme.color['fill-surface-default'],
    blockSize: HEIGHT.SELECT_MENU_INPUT,
    borderBlockEnd: `${theme.space[25]} solid ${theme.color['border-core-default']}`,
    color: theme.color['text-core-default'],
    '& svg': {
      inlineSize: $iconSize,
      blockSize: $iconSize,
      marginInlineStart: theme.space[300]
    }
  };
});
export const StyledTextInput = styled.input.withConfig({
  displayName: "Selectstyles__StyledTextInput",
  componentId: "cam4x1-11"
})(({
  theme
}) => {
  return Object.assign({
    inlineSize: '100%',
    paddingBlock: theme.space[200],
    paddingInlineEnd: theme.space[200],
    border: 'none',
    outline: 'none',
    fontSize: 'inherit',
    backgroundColor: theme.color['fill-surface-default'],
    '&::placeholder': {
      color: theme.color['text-core-subtle'],
      fontFamily: theme.typography['body-100'].fontFamily
    }
  }, getFocusStyleObject({
    theme
  }));
});
export const StyledMenu = styled.div.withConfig({
  displayName: "Selectstyles__StyledMenu",
  componentId: "cam4x1-12"
})(({
  theme,
  $isReferenceHidden
}) => {
  return {
    visibility: $isReferenceHidden ? 'hidden' : 'visible',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    outline: 0,
    backgroundColor: theme.color['fill-surface-default'],
    borderRadius: theme.borderRadius[200],
    boxShadow: theme.boxShadow[300],
    zIndex: JUPITER_LAYER
  };
});
export const StyledMenuOptionsWrapper = styled.div.withConfig({
  displayName: "Selectstyles__StyledMenuOptionsWrapper",
  componentId: "cam4x1-13"
})(({
  theme,
  $searchable
}) => {
  return Object.assign({
    borderRadius: 'inherit',
    flexGrow: 1,
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.color['fill-tertiary-pressed']} transparent`
  }, $searchable && {
    marginBlockStart: HEIGHT.SELECT_MENU_INPUT // usage of menu input height is intended
  });
});
export const StyledMenuOption = styled.div.withConfig({
  displayName: "Selectstyles__StyledMenuOption",
  componentId: "cam4x1-14"
})(({
  theme,
  $isActive,
  $disabled,
  $isSubheading,
  $divider
}) => {
  return Object.assign({
    paddingBlock: theme.space[300],
    paddingInline: theme.space[400],
    cursor: 'pointer',
    outline: 0
  }, $isSubheading && {
    padding: theme.space[200]
  }, $isActive && {
    backgroundColor: theme.color['fill-field-hover']
  }, $disabled && {
    cursor: 'not-allowed'
  }, !$disabled && {
    '&:active': {
      backgroundColor: theme.color['fill-tertiary-hover']
    }
  }, $divider && {
    borderBlockEndStyle: 'solid',
    borderBlockEndWidth: theme.borderWidth[100],
    borderBlockEndColor: theme.color['border-core-subtle']
  });
});
export const StyledMenuOptionContentWrapper = styled.div.withConfig({
  displayName: "Selectstyles__StyledMenuOptionContentWrapper",
  componentId: "cam4x1-15"
})(({
  theme,
  $optionTextColor
}) => {
  return {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    color: theme.color[$optionTextColor]
  };
});
export const StyledMenuOptionIndicator = styled.div.withConfig({
  displayName: "Selectstyles__StyledMenuOptionIndicator",
  componentId: "cam4x1-16"
})(({
  theme
}) => {
  return {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    blockSize: theme.space[600],
    borderRadius: theme.borderRadius['full']
  };
});
export const StyledHighlightText = styled.span.withConfig({
  displayName: "Selectstyles__StyledHighlightText",
  componentId: "cam4x1-17"
})(({
  theme
}) => {
  return {
    color: theme.color['text-interactive-default']
  };
});